import * as React from 'react'
import type { FormEvent, KeyboardEvent, ReactNode } from 'react'
import {
  BulletedListRules,
  OrderedListRules,
  unwrapList,
} from '@platejs/list-classic'
import { createBlockStartInputRule, KEYS } from 'platejs'
import { PlateElement, useEditorRef, usePlateEditor } from 'platejs/react'
import type { PlateElementProps } from 'platejs/react'

import {
  SendHorizontalIcon,
  AtSignIcon,
  ArrowLeftIcon,
  Avatar,
  Button,
  Collapsible,
  IconButton,
  SmileIcon,
  TextInitialIcon,
  Toolbar,
} from '@buffer-mono/popcorn'
import {
  BoldToolbarButton,
  ItalicToolbarButton,
  StrikethroughToolbarButton,
  LinkPopover,
  LinkToolbarButton,
  RichTextField,
  InlineCombobox,
  RichTextFieldBoldPlugin,
  RichTextFieldH3Plugin,
  RichTextFieldItalicPlugin,
  RichTextFieldParagraphPlugin,
  RichTextFieldStrikethroughPlugin,
} from '@buffer-mono/popcorn/rich-text'
import {
  BulletedListToolbarButton,
  NumberedListToolbarButton,
  RichTextFieldBulletedListPlugin,
  RichTextFieldListItemContentPlugin,
  RichTextFieldListItemPlugin,
  RichTextFieldListPlugin,
  RichTextFieldNumberedListPlugin,
} from '@buffer-mono/popcorn/rich-text/lists'
import { createRichTextFieldMentionKit } from '@buffer-mono/popcorn/rich-text/mention'
import { MentionElement } from '@buffer-mono/popcorn/rich-text/mention'
import type { RichTextFieldMentionElement } from '@buffer-mono/popcorn/rich-text/mention'

import { EmojiPicker } from '~publish/components/EmojiPicker'
import { useSelectableTeamMembers } from '~publish/hooks/notes/useSelectableTeamMembers'
import type { TeamMemberScope } from '~publish/hooks/notes/selectableTeamMembersQuery'

import { fromNoteContent, HEADING_KEY, toNoteContent } from './noteRichContent'
import { NoteLinkPlugin } from './noteLinkPlugin'
import type { NoteContent } from './noteRichContent'
import { useNoteMentionsEnabled } from './useNoteMentionsEnabled'

import styles from './NoteRichEditor.module.css'

const MentionScopeContext = React.createContext<TeamMemberScope | null>(null)

function MentionInput({
  children,
  element,
  ...props
}: PlateElementProps): React.ReactElement {
  const editor = useEditorRef()
  const scope = React.useContext(MentionScopeContext)
  const [search, setSearch] = React.useState('')
  const { members, loading, error } = useSelectableTeamMembers(scope, search)
  const selectableMembers = members
    .map((member) => ({
      id: member.id,
      urn: member.urn,
      email: member.email,
      avatar: member.avatar,
      name: member.name?.trim() || member.email,
    }))
    .filter((member) => member.urn && member.name)
  const trigger = typeof element.trigger === 'string' ? element.trigger : '@'
  return (
    <PlateElement {...props} element={element} as="span">
      <InlineCombobox.Root
        trigger={trigger}
        value={search}
        onValueChange={setSearch}
      >
        <InlineCombobox.Input aria-label="Search teammates" />
        <InlineCombobox.Content>
          <InlineCombobox.List aria-label="Teammates">
            {selectableMembers.map((member) => (
              <InlineCombobox.Item
                key={member.id}
                value={member.id}
                textValue={member.name}
                onSelect={() => {
                  if (!member.urn || !member.name) return
                  const mention: RichTextFieldMentionElement = {
                    type: 'mention',
                    key: member.id,
                    trigger,
                    value: member.name,
                    displayValue: member.name,
                    serviceEntityId: member.urn,
                    children: [{ text: '' }],
                  }
                  editor.tf.insertNodes(mention, { select: true })
                  editor.tf.move({ unit: 'offset' })
                  editor.tf.insertText(' ')
                }}
              >
                <span className={styles.mentionItem}>
                  <Avatar
                    src={member.avatar?.trim() || undefined}
                    alt=""
                    fallback="letter"
                    fallbackName={member.name}
                    size="xxsmall"
                  />
                  <span>{member.name}</span>
                </span>
              </InlineCombobox.Item>
            ))}
          </InlineCombobox.List>
          {(error || loading || selectableMembers.length === 0) && (
            <InlineCombobox.Status>
              {error
                ? 'Could not load teammates.'
                : loading
                  ? 'Loading teammates…'
                  : 'No teammates found.'}
            </InlineCombobox.Status>
          )}
        </InlineCombobox.Content>
      </InlineCombobox.Root>
      {children}
    </PlateElement>
  )
}

function NoteMentionElement(
  props: PlateElementProps<RichTextFieldMentionElement>,
): React.ReactElement {
  const marks = props.element.children[0]
  const className = [
    marks?.bold ? styles.boldMention : '',
    marks?.italic ? styles.italicMention : '',
    marks?.strikethrough ? styles.strikethroughMention : '',
  ]
    .filter(Boolean)
    .join(' ')
  return <MentionElement {...props} className={className} />
}

const baseMentionKit = createRichTextFieldMentionKit({
  inputComponent: MentionInput,
})
const mentionKit = [
  baseMentionKit[0].withComponent(NoteMentionElement),
  baseMentionKit[1],
]
// A Note has one heading level, so every `#` prefix makes the same heading.
const NoteHeadingPlugin = RichTextFieldH3Plugin.configure({
  inputRules: [
    createBlockStartInputRule({
      match: /^#{1,3}$/,
      trigger: ' ',
      apply: ({ editor }, match) => {
        editor.tf.delete({ at: match.range })
        unwrapList(editor)
        editor.tf.setNodes({ type: HEADING_KEY })
        return true
      },
    }),
  ],
})

// Notes store flat lists only. Returning false from `tab` inside a list skips
// the list indent and lets the browser move focus, as elsewhere in the form.
const NoteListPlugin = RichTextFieldListPlugin.extend({
  plugins: [
    RichTextFieldBulletedListPlugin.configure({
      inputRules: [
        BulletedListRules.markdown(),
        BulletedListRules.markdown({ variant: '*' }),
      ],
    }),
    RichTextFieldNumberedListPlugin.configure({
      inputRules: [
        OrderedListRules.markdown(),
        OrderedListRules.markdown({ variant: ')' }),
      ],
    }),
    RichTextFieldListItemPlugin,
    RichTextFieldListItemContentPlugin,
  ],
}).overrideEditor(({ editor, tf: { tab } }) => ({
  transforms: {
    tab(options): boolean | undefined {
      if (editor.api.some({ match: { type: editor.getType(KEYS.li) } }))
        return false
      return tab(options)
    },
  },
}))

const basePlugins = [
  RichTextFieldParagraphPlugin,
  NoteHeadingPlugin,
  NoteListPlugin,
  RichTextFieldBoldPlugin,
  RichTextFieldItalicPlugin,
  RichTextFieldStrikethroughPlugin,
  NoteLinkPlugin,
]
const plugins = [...basePlugins, ...mentionKit]

type ContextValue = {
  content: NoteContent
  disabled: boolean
  errorId: string
  submitDisabled: boolean
  mentionsEnabled: boolean
  expanded: boolean
}
const Context = React.createContext<ContextValue | null>(null)
const useNoteRichEditor = (): ContextValue => {
  const context = React.useContext(Context)
  if (!context)
    throw new Error('NoteRichEditor parts require a NoteRichEditor parent')
  return context
}

export type NoteRichEditorProps = {
  initialContent: NoteContent
  onChange?: (content: NoteContent) => void
  /**
   * `clear` empties this editor in place, keeping its caret, and returns
   * true. It returns false once the editor has been replaced or edited, so
   * the caller discards its stored draft itself.
   */
  onSubmit: (content: NoteContent, clear: () => boolean) => void
  disabled?: boolean
  pending?: boolean
  submitBlocked?: boolean
  children: ReactNode
  mentionScope?: TeamMemberScope | null
  /** Show only the field until it is focused or holds a draft. */
  collapseWhenIdle?: boolean
}

function Root({
  initialContent,
  onChange,
  onSubmit,
  disabled = false,
  pending = false,
  submitBlocked = false,
  children,
  mentionScope = null,
  collapseWhenIdle = false,
}: NoteRichEditorProps): React.ReactElement {
  const mentionsEnabled = useNoteMentionsEnabled()
  const editor = usePlateEditor(
    {
      plugins: mentionsEnabled ? plugins : basePlugins,
      value: fromNoteContent(initialContent),
    },
    [mentionsEnabled],
  )
  const [content, setContent] = React.useState(initialContent)
  const latestContent = React.useRef(content)
  latestContent.current = content
  const mounted = React.useRef(false)
  React.useEffect(() => {
    mounted.current = true
    return (): void => {
      mounted.current = false
    }
  }, [])
  const [focused, setFocused] = React.useState(false)
  const collapseTimer = React.useRef<ReturnType<typeof setTimeout>>()
  const formRef = React.useRef<HTMLFormElement>(null)
  const errorId = React.useId()
  const expanded = !collapseWhenIdle || focused || content.text.trim() !== ''
  const submitDisabled =
    disabled ||
    pending ||
    submitBlocked ||
    content.text.length > 500 ||
    !content.text.trim()
  const context = React.useMemo(
    () => ({
      content,
      disabled,
      errorId,
      submitDisabled,
      mentionsEnabled,
      expanded,
    }),
    [content, disabled, errorId, expanded, mentionsEnabled, submitDisabled],
  )
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (submitDisabled) return
    // Activating Send from the footer returns the caret to the field, as a
    // click does. The button disables itself while the request is pending,
    // and a focused control that disables drops focus to the body.
    if (
      !editor.api.isFocused() &&
      formRef.current?.contains(document.activeElement)
    ) {
      editor.tf.focus()
    }
    const submitted = content
    onSubmit(submitted, () => {
      // Text typed while the request was pending is not the sent note.
      if (!mounted.current || latestContent.current !== submitted) return false
      // A full reset drops the pending operations, and with them the
      // onValueChange that reports the empty draft. Replacing children keeps it.
      editor.tf.reset({ children: true })
      if (editor.api.isFocused()) editor.tf.focus({ edge: 'startEditor' })
      return true
    })
  }
  const onFocus = (): void => {
    clearTimeout(collapseTimer.current)
    setFocused(true)
  }
  // The emoji picker and link popover are portaled, yet their focus events
  // still bubble here through the React tree. Let that focus arrive before
  // deciding, or collapsing would unmount the overlay mid-click.
  const onBlur = (): void => {
    collapseTimer.current = setTimeout(() => {
      // Losing the window is not leaving the editor. Focus returns to the
      // field with the window, so collapsing here would only flash.
      if (document.hasFocus()) setFocused(false)
    }, 0)
  }
  return (
    <MentionScopeContext.Provider value={mentionScope}>
      <Context.Provider value={context}>
        <form
          ref={formRef}
          className={styles.form}
          onSubmit={submit}
          autoComplete="off"
          onFocus={onFocus}
          onBlur={onBlur}
        >
          <RichTextField.Root
            editor={editor}
            className={styles.editor}
            disabled={disabled}
            data-expanded={expanded || undefined}
            onValueChange={({ value }) => {
              const nextContent = toNoteContent(value)
              setContent(nextContent)
              onChange?.(nextContent)
            }}
          >
            {children}
          </RichTextField.Root>
        </form>
      </Context.Provider>
    </MentionScopeContext.Provider>
  )
}

const onFieldKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
  if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
    event.preventDefault()
    event.currentTarget.closest('form')?.requestSubmit()
  }
}

function Field({
  label,
  placeholder,
  fieldRef,
  focusOnMount,
}: {
  label: string
  placeholder?: string
  fieldRef?: React.Ref<HTMLDivElement>
  focusOnMount?: boolean
}): React.ReactElement {
  const { content } = useNoteRichEditor()
  const editor = useEditorRef()
  React.useEffect(() => {
    if (!focusOnMount) return
    editor.tf.select(editor.api.end([]))
    editor.tf.focus()
  }, [focusOnMount, editor])
  return (
    <RichTextField.Editable
      ref={fieldRef}
      className={styles.field}
      aria-label={label}
      aria-invalid={content.text.length > 500}
      placeholder={placeholder}
      onKeyDownCapture={onFieldKeyDown}
    />
  )
}

function Footer({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}): React.ReactElement {
  const { expanded } = useNoteRichEditor()
  return (
    <Collapsible open={expanded}>
      <Collapsible.Content>
        <RichTextField.Footer className={`${styles.footer} ${className ?? ''}`}>
          {children}
        </RichTextField.Footer>
      </Collapsible.Content>
    </Collapsible>
  )
}

function EmojiButton(): React.ReactElement {
  const editor = useEditorRef()
  return (
    <EmojiPicker
      onEmojiSelect={(emoji) => {
        editor.tf.insertText(emoji)
        editor.tf.focus()
      }}
    >
      <IconButton
        label="Insert emoji"
        variant="tertiary"
        type="button"
        size="small"
      >
        <SmileIcon />
      </IconButton>
    </EmojiPicker>
  )
}

function MentionButton(): React.ReactElement | null {
  const editor = useEditorRef()
  const scope = React.useContext(MentionScopeContext)
  const { disabled, mentionsEnabled } = useNoteRichEditor()
  if (!mentionsEnabled) return null
  return (
    <IconButton
      label="Mention teammate"
      variant="tertiary"
      size="small"
      type="button"
      disabled={!scope || disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        editor.tf.focus()
        editor.tf.insertText('@')
      }}
    >
      <AtSignIcon />
    </IconButton>
  )
}

function Formatting(): React.ReactElement {
  const [showFormatting, setShowFormatting] = React.useState(false)
  const { disabled } = useNoteRichEditor()
  const focusTarget = React.useRef<'back' | 'opener' | null>(null)
  const backRef = React.useRef<HTMLButtonElement>(null)
  const openerRef = React.useRef<HTMLButtonElement>(null)

  React.useLayoutEffect(() => {
    if (focusTarget.current === 'back') backRef.current?.focus()
    if (focusTarget.current === 'opener') openerRef.current?.focus()
    focusTarget.current = null
  }, [showFormatting])

  return (
    <div className={styles.formatting}>
      {showFormatting ? (
        <Toolbar
          aria-label="Note formatting"
          className={styles.formattingToolbar}
        >
          <Toolbar.Button asChild>
            <IconButton
              ref={backRef}
              label="Back to note actions"
              tooltip="Back to note actions"
              variant="tertiary"
              type="button"
              size="small"
              onClick={() => {
                focusTarget.current = 'opener'
                setShowFormatting(false)
              }}
            >
              <ArrowLeftIcon />
            </IconButton>
          </Toolbar.Button>
          <BoldToolbarButton size="small" tooltip="Bold" />
          <ItalicToolbarButton size="small" tooltip="Italic" />
          <StrikethroughToolbarButton size="small" tooltip="Strikethrough" />
          <BulletedListToolbarButton size="small" tooltip="Bulleted list" />
          <NumberedListToolbarButton size="small" tooltip="Numbered list" />
          <LinkPopover>
            <LinkToolbarButton size="small" tooltip="Link" />
          </LinkPopover>
        </Toolbar>
      ) : (
        <>
          <MentionButton />
          <EmojiButton />
          <IconButton
            ref={openerRef}
            label="Open formatting"
            tooltip="Open formatting"
            variant="tertiary"
            type="button"
            size="small"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              focusTarget.current = 'back'
              setShowFormatting(true)
            }}
          >
            <TextInitialIcon />
          </IconButton>
        </>
      )}
    </div>
  )
}

function Actions({ children }: { children: ReactNode }): React.ReactElement {
  return <div className={styles.actions}>{children}</div>
}

function Submit({
  label = 'Send note',
}: {
  label?: string
}): React.ReactElement {
  const { submitDisabled } = useNoteRichEditor()
  return (
    <IconButton
      label={label}
      type="submit"
      variant="primary"
      disabled={submitDisabled}
      // Keep the caret in the field so the next note can follow at once.
      onMouseDown={(event) => event.preventDefault()}
    >
      <SendHorizontalIcon />
    </IconButton>
  )
}

function Save(): React.ReactElement {
  const { submitDisabled } = useNoteRichEditor()
  return (
    <Button
      type="submit"
      variant="secondary"
      size="small"
      disabled={submitDisabled}
    >
      Save
    </Button>
  )
}

function Counter(): React.ReactElement | null {
  const { content } = useNoteRichEditor()
  const remaining = 500 - content.text.length
  if (remaining > 100) return null
  return (
    <output
      className={styles.counter}
      data-over-limit={remaining < 0 || undefined}
      aria-label={
        remaining < 0
          ? `${-remaining} characters over the limit`
          : `${remaining} characters remaining`
      }
    >
      {content.text.length}/500
    </output>
  )
}

function ErrorMessage({
  children,
}: {
  children: ReactNode
}): React.ReactElement {
  const { errorId } = useNoteRichEditor()
  return (
    <div id={errorId} className={styles.error} role="alert">
      {children}
    </div>
  )
}

export const NoteRichEditor = Object.assign(Root, {
  Field,
  Footer,
  EmojiButton,
  MentionButton,
  Formatting,
  Actions,
  Submit,
  Save,
  Counter,
  Error: ErrorMessage,
})
